import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import TngRewardCanvas from '@/components/survey/TngRewardCanvas';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2Complete } from '@/lib/q7_2Storage';
import { isQ7_3Complete } from '@/lib/q7_3Storage';
import { isQ7_4CompleteForSurvey } from '@/lib/q7_4Storage';
import { isQ7_5Complete } from '@/lib/q7_5Storage';
import { isQ7_6Complete } from '@/lib/q7_6Storage';
import { readTngRewardSession } from '@/lib/tngRewardSession';
import { isOtherBrandsComplete } from '@/lib/otherBrandsStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

/** Browser back/forward POP from /tng → home (BrowserRouter has no useBlocker). */
function TngBackToHomeOnPop() {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    const onPopState = () => {
      navigate('/', { replace: true });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigate]);
  return null;
}

function formatExpiryLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

export default function Tng() {
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const codeBrand = useAppSelector(selectCodeBrand);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const reward = useMemo(() => readTngRewardSession(), []);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  /**
   * After survey-submit-tng we call `clearAllLocalStorage()` before `/tng`. Full session +
   * Redux reset runs when leaving `/tng` (see router). `sessionStorage` still holds
   * `tng_reward` until then — skip localStorage-based step checks when set.
   */
  if (reward == null) {
    if (!isSmokerProfileComplete()) {
      return <Navigate to="/smokerprofile" replace />;
    }
    if (!isOtherBrandsComplete()) {
      return <Navigate to="/otherbrands" replace />;
    }
    if (!isQ7_1Complete()) {
      return <Navigate to="/7_1" replace />;
    }
    if (!isQ7_2Complete()) {
      return <Navigate to="/7_2" replace />;
    }
    if (!isQ7_3Complete()) {
      return <Navigate to="/7_3" replace />;
    }
    if (!isQ7_4CompleteForSurvey()) {
      return <Navigate to="/7_4" replace />;
    }
    // LBS continues through /7_5 and /7_6; Dice ends at /7_4.
    if (codeBrand === 'LBS') {
      if (!isQ7_5Complete()) {
        return <Navigate to="/7_5" replace />;
      }
      if (!isQ7_6Complete()) {
        return <Navigate to="/7_6" replace />;
      }
    }
  }

  const expiryLabel = formatExpiryLabel(reward?.expirydate ?? null);
  const valueLabel = reward?.value?.trim() ? reward.value.trim() : '-';

  useEffect(() => {
    if (reward != null) {
      clearAllLocalStorage();
    }
  }, [reward]);

  return (
    <>
      <TngBackToHomeOnPop />
      <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0 px-6 pt-8 text-center sm:pt-10">
          <h1 className="text-[36px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[42px]">
            {t.tng.title}
          </h1>
          <p className="mt-2 text-[22px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[28px]">
            {t.tng.subtitle}
          </p>
        </div>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-10 pt-6">
          {reward != null ? (
            <div className="flex flex-col items-center gap-10 sm:gap-11 -translate-y-[5%]">
              <TngRewardCanvas
                pin={reward.pin}
                valueLabel={reward.value}
                expiryLabel={expiryLabel}
              />
              <p className="max-w-md px-2 text-center text-[22px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[28px]">
                {t.tng.smsNote}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center -translate-y-[5%]">
              <img
                src="/images/TnG.jpg"
                alt=""
                className="max-h-[min(65.78vh,557px)] w-full max-w-[567px] rounded-2xl object-contain"
              />
              <p className="mt-4 max-w-md text-center text-[14px] leading-snug text-white/85">
                Your voucher could not be loaded in this view. If you just completed the survey,
                open this page from the trade-in step again in the same browser tab.
              </p>
            </div>
          )}
        </div>
      </div>
    </SurveyBackgroundLayout>
    </>
  );
}
